import { updateUserById, addFcmToken } from 'API/user'
import { generateToken } from 'utils/firebase'
import { rootStore } from 'stores'

class FCMTokenManager {
  private isInitialized = false
  private currentToken: string | null = null

  /**
   * Initialize FCM token for the current user
   * Should be called after user authentication
   */
  public async initializeToken(userId: number): Promise<void> {
    if (this.isInitialized) {
      console.log('FCM Token already initialized')
      return
    }

    try {
      // Check if Firebase is properly configured
      if (!('Notification' in window)) {
        console.warn('This browser does not support notifications')
        return
      }

      // Check if Service Worker is registered
      if (!('serviceWorker' in navigator)) {
        console.warn('This browser does not support service workers')
        return
      }

      // Wait for Service Worker to be ready
      await this.waitForServiceWorker()

      // Request notification permission first
      const permission = await Notification.requestPermission()

      if (permission !== 'granted') {
        console.warn('Notification permission not granted')
        return
      }

      // Generate FCM token with retry
      const token = await this.generateTokenWithRetry()

      if (token) {
        this.currentToken = token

        // Save token to user profile
        await this.saveTokenToUser(userId, token)

        // Store in auth store for quick access
        rootStore.authStore.setFCMToken(token)

        this.isInitialized = true
        console.log('FCM Token initialized successfully:', token + '...')
      } else {
        console.warn('Failed to generate FCM token - check Firebase configuration')
      }
    } catch (error) {
      console.error('Error initializing FCM token:', error)
    }
  }

  private async waitForServiceWorker(): Promise<void> {
    console.log('Waiting for Service Worker to be ready...')

    let attempts = 0
    const maxAttempts = 10

    while (attempts < maxAttempts) {
      const registrations = await navigator.serviceWorker.getRegistrations()
      const rootScopeSW = registrations.find(
        reg => reg.scope === window.location.origin + '/' && reg.active && reg.active.state === 'activated'
      )

      if (rootScopeSW) {
        console.log('Service Worker is ready:', rootScopeSW.scope)
        return
      }

      console.log(`Service Worker not ready, attempt ${attempts + 1}/${maxAttempts}`)
      await new Promise(resolve => setTimeout(resolve, 1000))
      attempts++
    }

    // If no Service Worker found, try to register one
    console.log('No Service Worker found, attempting to register...')
    try {
      await navigator.serviceWorker.register('/firebase-messaging-sw.js', { scope: '/' })
      console.log('Service Worker registered, waiting for activation...')

      // Wait additional time for activation
      await new Promise(resolve => setTimeout(resolve, 3000))
    } catch (error) {
      console.error('Failed to register Service Worker:', error)
      throw error
    }
  }

  private async generateTokenWithRetry(): Promise<string | null> {
    let attempts = 0
    const maxAttempts = 3

    while (attempts < maxAttempts) {
      try {
        console.log(`Attempting to generate FCM token (attempt ${attempts + 1}/${maxAttempts})`)
        const token = await generateToken()

        if (token) {
          console.log('FCM token generated successfully')
          return token
        }
      } catch (error) {
        console.error(`Token generation attempt ${attempts + 1} failed:`, error)

        if (attempts < maxAttempts - 1) {
          console.log('Waiting before retry...')
          await new Promise(resolve => setTimeout(resolve, 2000))
        }
      }

      attempts++
    }

    console.error('All token generation attempts failed')
    return null
  }

  /**
   * Update FCM token for user
   */
  private async saveTokenToUser(userId: number, token: string): Promise<void> {
    try {
      // Delay 1 second before calling API
      await new Promise(resolve => setTimeout(resolve, 1000))

      await addFcmToken(userId, token)
      console.log('FCM token saved to user profile successfully')
    } catch (error) {
      console.error('CRITICAL: Failed to save FCM token to user profile:', error)

      // Show user-friendly error notification
      if (typeof window !== 'undefined' && 'Notification' in window) {
        console.warn('FCM token could not be saved - real-time notifications may not work')
      }

      throw error
    }
  }

  /**
   * Refresh FCM token if needed
   */
  public async refreshToken(userId: number): Promise<void> {
    try {
      const newToken = await generateToken()
      if (newToken && newToken !== this.currentToken) {
        this.currentToken = newToken
        await this.saveTokenToUser(userId, newToken)
        rootStore.authStore.setFCMToken(newToken)
        console.log('FCM token refreshed')
      }
    } catch (error) {
      console.error('Error refreshing FCM token:', error)
    }
  }

  /**
   * Get current FCM token
   */
  public getCurrentToken(): string | null {
    return this.currentToken
  }

  /**
   * Clear token when user logs out
   */
  public clearToken(): void {
    this.currentToken = null
    this.isInitialized = false
    rootStore.authStore.setFCMToken('')
    console.log('FCM token cleared')
  }

  /**
   * Check if FCM is supported and permission is granted
   */
  public async checkFCMSupport(): Promise<boolean> {
    if (!('Notification' in window)) {
      console.warn('This browser does not support notifications')
      return false
    }

    if (!('serviceWorker' in navigator)) {
      console.warn('This browser does not support service workers')
      return false
    }

    return true
  }
}

export const fcmTokenManager = new FCMTokenManager()
